'use client';

import * as React from 'react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  MailIcon,
  UsersRoundIcon
} from 'lucide-react';

import { Button } from '@workspace/ui/components/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@workspace/ui/components/collapsible';

import { COMMUNITY_LINKS } from '~/components/marketing-links';
import { NewsletterSignupForm } from '~/components/newsletter-signup-form';
import { useMarketingAnalytics } from '~/hooks/use-marketing-analytics';

export function CommunityConnectCard(): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  const { trackDiscordClick, trackGithubClick } = useMarketingAnalytics();

  const handleCommunityClick = (name: string): void => {
    if (name === 'Discord') {
      trackDiscordClick('community_panel_discord');
    }

    if (name === 'GitHub') {
      trackGithubClick('community_panel_github');
    }
  };

  return (
    <div className="space-y-5 rounded-[2rem] border border-border/70 bg-background p-6 shadow-sm">
      <p className="text-sm leading-7 text-muted-foreground">
        Get the important updates by email, or choose the public channel that
        fits how you follow the build.
      </p>

      <NewsletterSignupForm
        inputId="community-updates-email"
        source="community_updates_form"
        buttonLabel="Email for updates"
        className="max-w-none"
        inputClassName="rounded-xl"
        buttonClassName="rounded-xl"
      />

      <Collapsible
        open={open}
        onOpenChange={setOpen}
      >
        <CollapsibleTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="h-11 w-full justify-center gap-2 rounded-xl"
          >
            <UsersRoundIcon className="size-4 shrink-0" />
            Join our Community
            {open ? (
              <ChevronUpIcon className="size-4 shrink-0" />
            ) : (
              <ChevronDownIcon className="size-4 shrink-0" />
            )}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="mt-3 grid gap-2 rounded-2xl border border-border/70 bg-muted/20 p-2 sm:grid-cols-2">
            {COMMUNITY_LINKS.map((item) =>
              item.href ? (
                <a
                  key={item.name}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleCommunityClick(item.name)}
                  className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition hover:bg-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span className="flex size-8 items-center justify-center rounded-full bg-background text-foreground">
                    {item.icon}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold">
                      {item.name}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {item.handle}
                    </span>
                  </span>
                </a>
              ) : (
                <div
                  key={item.name}
                  aria-disabled="true"
                  className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-left opacity-60"
                >
                  <span className="flex size-8 items-center justify-center rounded-full bg-background text-muted-foreground">
                    {item.icon}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold">
                      {item.name}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {item.handle}
                    </span>
                  </span>
                </div>
              )
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      <div className="flex items-center gap-3 border-t border-border/70 pt-5 text-sm text-muted-foreground">
        <MailIcon className="size-4 shrink-0" />
        Major milestones, not day-to-day noise.
      </div>
    </div>
  );
}
